import { useParams, useNavigate } from 'react-router-dom'
import { warn } from '../utils/logger'
import {
    Container,
    Title,
    Text,
    LoadingOverlay,
    Grid,
    Card,
    Badge,
    Group,
    Stack,
    Image,
    Button,
    Divider,
    Box, Modal,
    NumberInput,
    Paper,
    Collapse,
    ActionIcon
} from '@mantine/core'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { IconExternalLink, IconHeart, IconTrendingUp, IconEdit, IconTrash, IconPlus, IconChevronDown, IconPhoto } from '@tabler/icons-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { gamesApi, wishlistApi, pricesApi, productIdsApi, analyticsApi } from '../services/api'
import { Game, GamePlatform, Price, WishlistItem } from '../types'
import { formatPrice } from '../utils/format'
import { useState, useEffect, useMemo } from 'react'
import { useMediaQuery } from '@mantine/hooks'
import { SEOHead } from '../components/SEOHead'
import { HorizontalGameScroller } from '../components/HorizontalGameScroller'
import { CommentSection } from '../components/CommentSection'
import { StoreRating } from '../components/StoreRating'
import { useAuth } from '@/hooks/useAuth'
import { useI18n } from '@/hooks/useI18n'
import { getPlatformColor } from '../utils/platform-colors'
import { IconSettings } from '@tabler/icons-react'
import { GameRating } from '../components/GameRating'
import { ImageBrowserModal } from '../components/ImageBrowserModal'
import { GameDetailPageSkeleton } from '../components/GameDetailPageSkeleton'
import { notifications } from '@mantine/notifications'

export const GameDetailPage: React.FC<{ slug?: string; platformSlug?: string }> = ({
    slug: propSlug,
    platformSlug: propPlatformSlug
}) => {
    const params = useParams<{ slug: string; platformSlug?: string }>()
    const slug = propSlug || params.slug
    const platformSlug = propPlatformSlug || params.platformSlug
    const navigate = useNavigate()
    const { t, country } = useI18n()
    const { isAuthenticated, isAdmin } = useAuth()
    const queryClient = useQueryClient()
    const isLargeScreen = useMediaQuery('(min-width: 768px)')
    const [priceHistoryModal, setPriceHistoryModal] = useState<{ open: boolean; storeId?: string; storeName?: string }>({ open: false })
    const [selectedStore, setSelectedStore] = useState<{ storeId: string; storeName: string } | null>(null)
    const [wishlistModal, setWishlistModal] = useState<{ open: boolean; maxPrice: number; isEditing: boolean }>({
        open: false,
        maxPrice: 0,
        isEditing: false
    })

    const [platformModal, setPlatformModal] = useState(false)
    const [descriptionExpanded, setDescriptionExpanded] = useState(false)
    const [ratingModal, setRatingModal] = useState<{ open: boolean; storeId?: string; storeName?: string }>({ open: false })

    // Image browser modal state
    const [imageBrowserModalOpened, setImageBrowserModalOpened] = useState(false)



    const { data: gameData, isLoading, error } = useQuery({
        queryKey: ['game', slug, platformSlug, country],
        queryFn: () => platformSlug
            ? gamesApi.getGameDetail(slug!, platformSlug, country)
            : gamesApi.getGame(slug!, platformSlug, country),
        enabled: !!slug,
        select: (response) => response.data.data,
    })

    // Handle both Game and GamePlatform types
    const isGamePlatform = gameData ? 'gameId' in gameData : false
    const game = isGamePlatform ? (gameData as GamePlatform).game : gameData as Game
    const currentPlatform = isGamePlatform ? (gameData as GamePlatform).platform : null

    // Get game platforms for tab switching from optimized endpoint or fallback query
    const { data: gamePlatformsFallback } = useQuery({
        queryKey: ['game-platforms', slug, country],
        queryFn: () => gamesApi.getGamePlatforms(slug!, country),
        enabled: !!slug && !platformSlug, // Only fetch if no platform specified (fallback)
        select: (response) => response.data.data,
    })

    // Use platforms from optimized endpoint or fallback
    const gamePlatforms = isGamePlatform && gameData && (gameData as any).allPlatforms
        ? (gameData as any).allPlatforms
        : gamePlatformsFallback

    // Check wishlist for current country
    const { data: wishlistData, refetch: refetchWishlist } = useQuery({
        queryKey: ['wishlist-check', game?.id, currentPlatform?.id, country],
        queryFn: () => wishlistApi.checkWishlist(game!.id, currentPlatform?.id, country),
        enabled: !!(game?.id && isAuthenticated),
        select: (response) => response.data.data,
    })

    // Check wishlist across all countries
    const { data: allCountriesWishlistData } = useQuery({
        queryKey: ['wishlist-check-all-countries', game?.id, currentPlatform?.id],
        queryFn: () => wishlistApi.checkWishlistAllCountries(game!.id, currentPlatform?.id),
        enabled: !!(game?.id && isAuthenticated),
        select: (response) => response.data.data,
    })

    // Determine wishlist state for current country
    const wishlistItem = Array.isArray(wishlistData)
        ? wishlistData.find(item => item.platformId === currentPlatform?.id && item.country === country)
        : (wishlistData as WishlistItem | null)?.country === country ? wishlistData as WishlistItem | null : null

    const isInWishlist = !!wishlistItem && !wishlistItem.isOwned
    const isOwned = !!wishlistItem && wishlistItem.isOwned

    // Check if game is in wishlist in other countries
    const otherCountriesWishlistItems = allCountriesWishlistData?.filter(item =>
        item.country !== country && !item.isOwned
    ) || []

    const { data: priceHistory, isLoading: priceHistoryLoading } = useQuery({
        queryKey: ['price-history', game?.id, currentPlatform?.id, priceHistoryModal.storeId || selectedStore?.storeId, country],
        queryFn: () => pricesApi.getPriceHistory(
            game!.id,
            currentPlatform!.id,
            priceHistoryModal.storeId || selectedStore?.storeId,
            90 // 90 days of history
        ),
        enabled: Boolean((priceHistoryModal.open || (selectedStore && isLargeScreen)) && !!game?.id && !!currentPlatform?.id),
        select: (response) => response.data.data,
    })

    // Fetch product IDs for the current platform (fallback when not using optimized endpoint)
    const { data: productIdsFallback } = useQuery({
        queryKey: ['product-ids', game?.id, currentPlatform?.id, country],
        queryFn: () => productIdsApi.getGamePlatformProductIds(game!.id, currentPlatform?.id),
        enabled: !!(game?.id && currentPlatform?.id && !platformSlug),
        select: (response) => response.data.data,
    })

    // Fetch related games based on genre and platform (fallback when not using optimized endpoint)
    const { data: relatedGamesFallback } = useQuery({
        queryKey: ['related-games', game?.id, currentPlatform?.id],
        queryFn: () => gamesApi.getRelatedGames(game!.id, currentPlatform?.id, 10),
        enabled: !!game?.id && !platformSlug,
        select: (response) => response.data.data,
    })

    // Use data from optimized endpoint or fallback queries
    const productIds = isGamePlatform && gameData && (gameData as any).productIds
        ? (gameData as any).productIds
        : productIdsFallback

    // Ensure relatedGames is always in the correct GamePlatform format
    const relatedGames = useMemo(() => {
        const sourceData = isGamePlatform && gameData && (gameData as any).relatedGames
            ? (gameData as any).relatedGames
            : relatedGamesFallback;

        // Validate and filter the data to ensure it's in the correct format
        if (!Array.isArray(sourceData)) {
            console.warn('RelatedGames: Source data is not an array', sourceData);
            return [];
        }

        return sourceData.filter((item: any) => {
            const isValid = item &&
                item.game &&
                item.platform &&
                item.gameId &&
                item.platformId &&
                typeof item.game === 'object' &&
                typeof item.platform === 'object';

            if (!isValid) {
                console.warn('RelatedGames: Invalid item structure', item);
            }

            return isValid;
        });
    }, [isGamePlatform, gameData, relatedGamesFallback]);

    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
        console.log('GameDetailPage Debug:', {
            platformSlug,
            isGamePlatform,
            hasOptimizedData: !!(gameData && (gameData as any).relatedGames),
            relatedGamesLength: relatedGames?.length,
            relatedGamesType: Array.isArray(relatedGames) ? 'array' : typeof relatedGames,
            firstRelatedGame: relatedGames?.[0],
            gameDataKeys: gameData ? Object.keys(gameData) : null,
            relatedGamesSource: isGamePlatform && gameData && (gameData as any).relatedGames ? 'optimized' : 'fallback'
        });
    }

    // Mutation for updating game platform image
    const updateGamePlatformMutation = useMutation({
        mutationFn: ({ gameId, platformId, data }: { gameId: string; platformId: string; data: any }) =>
            gamesApi.updateGamePlatform(gameId, platformId, data),
        onSuccess: () => {
            notifications.show({
                title: 'Success',
                message: 'Platform image updated successfully',
                color: 'green'
            })
            // Invalidate the game data to refresh the image
            queryClient.invalidateQueries({ queryKey: ['game', slug, platformSlug, country] })
        },
        onError: (_error) => {
            notifications.show({
                title: 'Error',
                message: 'Failed to update platform image',
                color: 'red'
            })
        }
    })

    // Compute derived values
    const prices = useMemo(() => {
        return isGamePlatform ? (gameData as GamePlatform).prices || [] : []
    }, [isGamePlatform, gameData])

    const getLowestPrice = (platformPrices: Price[]) => {
        if (!platformPrices || !platformPrices.length) return null
        return platformPrices.reduce((lowest, price) =>
            parseFloat(price.amount.toString()) < parseFloat(lowest.amount.toString()) ? price : lowest
        )
    }

    const lowestPrice = getLowestPrice(prices)

    // Auto-select first store on large screens when prices are available
    useEffect(() => {
        if (isLargeScreen && prices.length > 0 && !selectedStore) {
            const firstStore = prices[0]?.store
            if (firstStore?.id && firstStore?.name) {
                setSelectedStore({ storeId: firstStore.id, storeName: firstStore.name })
            }
        }
    }, [isLargeScreen, prices, selectedStore])

    // Track page view when game loads
    useEffect(() => {
        if (game?.id) {
            // Generate or get session ID for tracking unique sessions
            let sessionId = sessionStorage.getItem('analytics_session_id')
            if (!sessionId) {
                sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
                sessionStorage.setItem('analytics_session_id', sessionId)
            }

            // Track page view asynchronously (fire and forget)
            analyticsApi.trackPageView({
                gameId: game.id,
                platformId: currentPlatform?.id,
                referrer: document.referrer || undefined,
                sessionId: sessionId,
            }).catch((error) => {
                // Silently fail - don't let tracking errors affect user experience
                warn('Failed to track page view:', error)
            })
        }
    }, [game?.id, currentPlatform?.id]) // Track when game or platform changes

    // Helper function to track store button clicks
    const trackStoreClick = (
        storeId: string,
        clickType: 'BEST_PRICE' | 'INDIVIDUAL_PRICE',
        priceAmount?: number,
        currency?: string
    ) => {
        if (!game?.id) return

        // Get session ID
        const sessionId = sessionStorage.getItem('analytics_session_id')

        // Track store click asynchronously (fire and forget)
        analyticsApi.trackStoreClick({
            gameId: game.id,
            platformId: currentPlatform?.id,
            storeId,
            clickType,
            priceAmount,
            currency,
            referrer: document.referrer || undefined,
            sessionId: sessionId || undefined,
        }).catch((error) => {
            // Silently fail - don't let tracking errors affect user experience
            warn('Failed to track store click:', error)
        })
    }

    // Clear selected store when switching to mobile
    useEffect(() => {
        if (!isLargeScreen && selectedStore) {
            setSelectedStore(null)
        }
    }, [isLargeScreen, selectedStore])

    // Helper function to check authentication and redirect if needed
    const requireAuthentication = (): boolean => {
        if (!isAuthenticated) {
            // Store current page path for redirect after login
            const currentPath = `/games/${slug}${platformSlug ? `/platforms/${platformSlug}` : ''}`
            localStorage.setItem('redirectAfterAuth', currentPath)
            navigate('/login')
            return false
        }
        return true
    }

    if (isLoading) {
        return <GameDetailPageSkeleton />
    }

    if (error || !gameData) {
        return (
            <Container size="xl" py="xl">
                <Title order={2}>{t('game.notFound')}</Title>
                <Text>{t('game.notFoundDescription')}</Text>
            </Container>
        )
    }

    const handleAddToWishlist = async () => {
        if (!requireAuthentication()) return
        if (!currentPlatform) return

        // Set initial max price and open modal
        const defaultMaxPrice = lowestPrice ? parseFloat(lowestPrice.amount.toString()) * 0.75 : 100
        setWishlistModal({ open: true, maxPrice: Math.floor(defaultMaxPrice), isEditing: false })
    }

    const handleEditWishlist = async () => {
        if (!requireAuthentication()) return
        if (!currentPlatform || !wishlistItem) return

        // Open modal with current maxPrice for editing
        const currentMaxPrice = parseFloat(wishlistItem.maxPrice.toString())
        setWishlistModal({ open: true, maxPrice: currentMaxPrice, isEditing: true })
    }

    const handleConfirmAddToWishlist = async () => {
        if (!requireAuthentication()) return
        if (!currentPlatform || wishlistModal.maxPrice <= 0) return

        try {
            if (wishlistModal.isEditing && wishlistItem) {
                // Update existing wishlist item
                await wishlistApi.updateWishlist(wishlistItem.id, { maxPrice: wishlistModal.maxPrice }, country)
            } else {
                // Add new wishlist item
                await wishlistApi.addToWishlist(game.id, currentPlatform.id, wishlistModal.maxPrice, false, country)
            }

            // Invalidate all wishlist-related cache entries
            queryClient.invalidateQueries({ queryKey: ['wishlist-check'] })
            refetchWishlist()
            setWishlistModal({ open: false, maxPrice: 0, isEditing: false })
        } catch (error) {
            console.error('Failed to add/update wishlist:', error)
        }
    }

    const handleAddToCollection = async () => {
        if (!requireAuthentication()) return
        if (!currentPlatform) return

        try {
            const maxPrice = lowestPrice ? lowestPrice.amount : 0 // Set to current price or 0 for owned games
            await wishlistApi.addToWishlist(game.id, currentPlatform.id, maxPrice, true, country)

            // Invalidate all wishlist-related cache entries
            queryClient.invalidateQueries({ queryKey: ['wishlist-check'] })
            refetchWishlist()
        } catch (error) {
            console.error('Failed to add to collection:', error)
        }
    }

    const handleRemoveFromWishlist = async () => {
        if (!requireAuthentication()) return
        if (!wishlistItem) return

        try {
            await wishlistApi.removeFromWishlist(wishlistItem.id, country)

            // Invalidate all wishlist-related cache entries
            queryClient.invalidateQueries({ queryKey: ['wishlist-check'] })
            refetchWishlist()
            setWishlistModal({ open: false, maxPrice: 0, isEditing: false })
        } catch (error) {
            console.error('Failed to remove from wishlist:', error)
        }
    }

    const handleToggleOwned = async () => {
        if (!requireAuthentication()) return
        if (!wishlistItem) return

        try {
            await wishlistApi.updateWishlist(wishlistItem.id, { isOwned: !wishlistItem.isOwned }, country)

            // Invalidate all wishlist-related cache entries
            queryClient.invalidateQueries({ queryKey: ['wishlist-check'] })
            refetchWishlist()
        } catch (error) {
            console.error('Failed to update wishlist:', error)
        }
    }

    const handlePlatformChange = (platformSlug: string | null) => {
        if (platformSlug) {
            navigate(`/games/${slug}/platforms/${platformSlug}`)
        }
    }

    const handleShowPriceHistory = (storeId: string, storeName: string) => {
        if (isLargeScreen) {
            setSelectedStore({ storeId, storeName })
        } else {
            setPriceHistoryModal({ open: true, storeId, storeName })
        }
    }

    const handleOpenRatingModal = (storeId: string, storeName: string) => {
        setRatingModal({ open: true, storeId, storeName })
    }

    const handleCloseRatingModal = () => {
        setRatingModal({ open: false })
    }

    // Image browser handlers
    const handleOpenImageBrowser = () => {
        setImageBrowserModalOpened(true)
    }

    const handleCloseImageBrowser = () => {
        setImageBrowserModalOpened(false)
    }

    const handleSelectImage = (imageUrl: string) => {
        if (game && currentPlatform) {
            updateGamePlatformMutation.mutate({
                gameId: game.id,
                platformId: currentPlatform.id,
                data: { imageUrl }
            })
        }
        setImageBrowserModalOpened(false)
    }

    const formatChartData = (prices: Price[]) => {
        return prices.map(price => ({
            date: new Date(price.recordedAt).toLocaleDateString(),
            price: parseFloat(price.amount.toString()),
            currency: price.currency,
        }))
    }

    const getCurrencySymbol = (currency: string) => {
        const symbols: { [key: string]: string } = {
            USD: '$',
            EUR: '€',
            GBP: '£',
            JPY: '¥',
        }
        return symbols[currency] || currency
    }

    const getRatingColor = (score: number) => {
        if (score >= 90) return 'green'
        if (score >= 80) return 'blue'
        if (score >= 70) return 'yellow'
        if (score >= 60) return 'orange'
        return 'red'
    }

    return (
        <>
            <SEOHead
                title={t('seo.game.title', { gameTitle: game.title, platformName: currentPlatform?.name })}
                description={game.description || t('seo.game.description', { gameTitle: game.title, platformName: currentPlatform?.name })}
                canonicalUrl={`https://spellen.land/games/${slug}${platformSlug ? `/platforms/${platformSlug}` : ''}`}
                image={(isGamePlatform && (gameData as GamePlatform).imageUrl) || undefined}
                type="product"
                game={game}
                gamePlatform={isGamePlatform ? (gameData as GamePlatform) : undefined}
                prices={prices}
            />

            <Container size="xl" py="xl">
                <Grid gutter="lg">
                    <Grid.Col span={{ base: 12, sm: isLargeScreen ? 3 : 12 }}>
                        {(isGamePlatform && (gameData as GamePlatform).imageUrl) && (
                            <Box style={{
                                maxWidth: isLargeScreen ? '280px' : '200px',
                                margin: isLargeScreen ? '0 auto 0 0' : '0 auto',
                                position: 'relative'
                            }}>
                                <Image
                                    src={(gameData as GamePlatform).imageUrl}
                                    alt={`${game.title}${currentPlatform ? ` on ${currentPlatform.name}` : ''}`}
                                    radius="md"
                                    fit="contain"
                                    w="100%"
                                    h="auto"
                                    fallbackSrc="https://placehold.co/300x383/dee2e6/6c757d?text=No+Cover"
                                />
                                {isAdmin && isGamePlatform && currentPlatform && (
                                    <ActionIcon
                                        variant="filled"
                                        color="blue"
                                        size="sm"
                                        style={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            zIndex: 10
                                        }}
                                        onClick={handleOpenImageBrowser}
                                        title="Change platform image"
                                    >
                                        <IconPhoto size={16} />
                                    </ActionIcon>
                                )}
                            </Box>
                        )}
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, sm: isLargeScreen ? 9 : 12 }}>
                        <Stack gap={isLargeScreen ? "sm" : "md"}>
                            <div>
                                <Group justify="space-between" align="flex-start">
                                    <div>
                                        <Title order={1} size="h1" mb="xs" fw={300}>{game.title}</Title>
                                        <Group gap="sm" align="center" wrap="wrap">
                                            {currentPlatform && (
                                                <Badge size="lg" variant="light" color={getPlatformColor(currentPlatform)}>
                                                    {currentPlatform.name}
                                                </Badge>
                                            )}
                                            {gameData.releaseDate && (
                                                <Text size="sm" c="dimmed">
                                                    {new Date(gameData.releaseDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                                                </Text>
                                            )}
                                        </Group>
                                    </div>
                                    {isAdmin && (
                                        <Button
                                            variant="light"
                                            color="grape"
                                            size="sm"
                                            leftSection={<IconSettings size={16} />}
                                            onClick={() => {
                                                if (currentPlatform) {
                                                    navigate(`/admin/games/${game.id}/edit?platform=${currentPlatform.slug}`)
                                                } else {
                                                    navigate(`/admin/games/${game.id}/edit`)
                                                }
                                            }}
                                            fullWidth={!isLargeScreen}
                                        >
                                            {t('game.adminEdit') || 'Admin Edit'}
                                        </Button>
                                    )}
                                </Group>
                            </div>

                            {game.description && (
                                <div>
                                    {(() => {
                                        const maxLength = 300 // Characters limit for truncation
                                        const shouldTruncate = game.description.length > maxLength
                                        const truncatedDescription = shouldTruncate
                                            ? game.description.substring(0, maxLength) + '...'
                                            : game.description

                                        if (!shouldTruncate) {
                                            return <Text size="md" color="dimmed">{game.description}</Text>
                                        }

                                        return (
                                            <div>
                                                <Text size="md" c="dimmed" mb="xs">
                                                    {truncatedDescription}
                                                </Text>
                                                <Collapse in={descriptionExpanded}>
                                                    <Text size="md" c="dimmed" mb="xs">
                                                        {game.description.substring(maxLength)}
                                                    </Text>
                                                </Collapse>
                                                <Button
                                                    variant="light"
                                                    size="sm"
                                                    onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                                                    p="xs"
                                                    h="auto"
                                                >
                                                    {descriptionExpanded
                                                        ? (t('common.showLess') || 'Show less')
                                                        : (t('common.showMore') || 'Show more')
                                                    }
                                                </Button>
                                            </div>
                                        )
                                    })()}
                                </div>
                            )}

                            <Group gap="lg">
                                {game.releaseDate && (
                                    <div>
                                        <Text size="sm" fw={500} c="dimmed">{t('game.releaseDate')}</Text>
                                        <Text size="md">{new Date(game.releaseDate).toLocaleDateString()}</Text>
                                    </div>
                                )}
                                {isGamePlatform && (gameData as GamePlatform).metacriticScore && (
                                    <div>
                                        <Text size="sm" fw={500} c="dimmed">{t('game.rating.igdb')}</Text>
                                        <Badge size="lg" color={getRatingColor((gameData as GamePlatform).metacriticScore!)} variant="light">
                                            {(gameData as GamePlatform).metacriticScore}/100 ({(gameData as GamePlatform).reviewCount || 0})
                                        </Badge>
                                    </div>
                                )}
                                {/* User Review Score and Button */}
                                {isGamePlatform && currentPlatform && (
                                    <div>
                                        <Group gap="xs">
                                            <GameRating
                                                gameSlug={game.slug}
                                                platformSlug={currentPlatform.slug}
                                                gameTitle={game.title}
                                                averageRating={((gameData as any)?.userReviewScore ?? 0)}
                                                totalRatings={((gameData as any)?.userReviewCount ?? 0)}
                                                compact
                                                showModal={false}
                                                onRateClick={() => setRatingModal({ open: true })}
                                            />
                                            <Button size="xs" variant="light" color="customGreen" onClick={() => setRatingModal({ open: true })}>
                                                {t('game.rating.rate')}
                                            </Button>
                                        </Group>
                                    </div>
                                )}
                            </Group>


                            {/* Platform Selection Buttons - Large Screens */}
                            {isLargeScreen && gamePlatforms && gamePlatforms.length > 1 && (
                                <div style={{ marginBottom: '8px' }}>
                                    <Group gap="sm" wrap="wrap">
                                        {gamePlatforms
                                            .filter((gamePlatform: any) =>
                                                gamePlatform.platform?.slug &&
                                                gamePlatform.platform.slug.trim() !== ''
                                            )
                                            .map((gamePlatform: any) => {
                                                const platformLowestPrice = getLowestPrice(gamePlatform.prices || [])
                                                const isCurrentPlatform = gamePlatform.platform.slug === platformSlug

                                                return (
                                                    <Button
                                                        key={gamePlatform.platform.slug}
                                                        variant={isCurrentPlatform ? "light" : "outline"}
                                                        color={getPlatformColor(gamePlatform.platform)}
                                                        size="md"
                                                        onClick={() => handlePlatformChange(gamePlatform.platform.slug)}
                                                        rightSection={
                                                            platformLowestPrice && (
                                                                <Badge
                                                                    size="md"
                                                                    color={getPlatformColor(gamePlatform.platform)}
                                                                >
                                                                    {formatPrice(platformLowestPrice.amount, platformLowestPrice.currency)}
                                                                </Badge>
                                                            )
                                                        }
                                                        styles={{
                                                            root: {
                                                                fontWeight: 500,
                                                                minWidth: 'fit-content',
                                                            }
                                                        }}
                                                    >
                                                        {gamePlatform.platform.name}
                                                    </Button>
                                                )
                                            })}
                                    </Group>
                                </div>
                            )}

                            {/* Lowest Price Display */}
                            {lowestPrice && (
                                <div>
                                    <Text size="sm" fw={500} c="dimmed">{t('game.bestPrice')}</Text>
                                    <Group align="center" gap="sm">
                                        <Badge size="xl" color="customGreen" variant="light">
                                            {formatPrice(lowestPrice.amount, lowestPrice.currency)}
                                        </Badge>
                                        {lowestPrice.storeUrl && (
                                            <Button
                                                component="a"
                                                href={lowestPrice.storeUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                variant="filled"
                                                color="customGreen"
                                                size="sm"
                                                rightSection={<IconExternalLink size={14} />}
                                                style={!isLargeScreen ? { flex: 1 } : undefined}
                                                onClick={() => {
                                                    if (lowestPrice.store?.id) {
                                                        trackStoreClick(
                                                            lowestPrice.store.id,
                                                            'BEST_PRICE',
                                                            parseFloat(lowestPrice.amount.toString()),
                                                            lowestPrice.currency
                                                        )
                                                    }
                                                }}
                                            >
                                                {t('game.buyNowAt', { store: lowestPrice.store?.name })}
                                            </Button>
                                        )}
                                    </Group>
                                </div>
                            )}

                            {/* Wishlist/Collection Actions */}
                            {currentPlatform && (
                                <>
                                    {isOwned ? (
                                        <Group>
                                            <Button
                                                variant="light"
                                                color="red"
                                                leftSection={<IconTrash size={16} />}
                                                onClick={handleRemoveFromWishlist}
                                                fullWidth={!isLargeScreen}
                                            >
                                                {t('game.removeFromCollection')}
                                            </Button>
                                        </Group>
                                    ) : isInWishlist ? (
                                        <Group gap="sm">
                                            <Button
                                                variant="outline"
                                                color="customGreen"
                                                leftSection={<IconEdit size={16} />}
                                                onClick={handleEditWishlist}
                                                fullWidth={!isLargeScreen}
                                            >
                                                {t('game.editWishlist', {
                                                    price: wishlistItem ? formatPrice(wishlistItem.maxPrice, lowestPrice?.currency || 'USD') : ''
                                                })}
                                            </Button>
                                            <Button
                                                variant="light"
                                                color="customGreen"
                                                leftSection={<IconPlus size={16} />}
                                                onClick={handleToggleOwned}
                                                fullWidth={!isLargeScreen}
                                            >
                                                {t('game.addToCollection')}
                                            </Button>
                                        </Group>
                                    ) : (
                                        <Group gap="sm">
                                            <Button
                                                variant="light"
                                                color="customGreen"
                                                leftSection={<IconHeart size={16} />}
                                                onClick={handleAddToWishlist}
                                                fullWidth={!isLargeScreen}
                                            >
                                                {t('game.addToWishlist')}
                                            </Button>
                                            <Button
                                                variant="light"
                                                color="customGreen"
                                                leftSection={<IconPlus size={16} />}
                                                onClick={handleAddToCollection}
                                                fullWidth={!isLargeScreen}
                                            >
                                                {t('game.addToCollection')}
                                            </Button>
                                        </Group>

                                    )}
                                </>
                            )}

                            {/* Show message if game is in wishlist in other countries */}
                            {otherCountriesWishlistItems.length > 0 && (
                                <Badge color="yellow" variant="light" size="lg">
                                    📍 {t('game.wishlistInOtherCountries', {
                                        countries: otherCountriesWishlistItems.map(item => item.country).join(', '),
                                        count: otherCountriesWishlistItems.length
                                    })}
                                </Badge>
                            )}

                            {game.genres && game.genres.length > 0 && (
                                <div>
                                    <Text size="sm" fw={500} c="dimmed" mb="xs">{t('game.genresLabel')}</Text>
                                    <Group gap="xs">
                                        {game.genres.map((gameGenre) => (
                                            <Badge key={gameGenre.genre?.id || Math.random()} variant="outline">
                                                {gameGenre.genre?.name || 'Unknown'}
                                            </Badge>
                                        ))}
                                    </Group>
                                </div>
                            )}

                        </Stack>
                    </Grid.Col>
                </Grid>

                <Divider my="xl" />

                {/* Platform Selection - Small Screens */}
                {!isLargeScreen && gamePlatforms && gamePlatforms.length > 1 && (
                    <Box mb="xl">
                        <Title order={2} mb="md">{t('game.selectPlatform')}</Title>
                        <Button
                            variant="outline"
                            size="md"
                            justify='space-between'
                            onClick={() => setPlatformModal(true)}
                            fullWidth
                            rightSection={<IconChevronDown size={16} />}
                            styles={{
                                root: {
                                    fontWeight: 500,
                                }
                            }}
                        >
                            {currentPlatform ? (
                                <Group justify="space-between" w="100%" mr="xl">
                                    <Text>{currentPlatform.name}</Text>
                                    {lowestPrice && (
                                        <Badge size="sm" color="customGreen" variant="light">
                                            {formatPrice(lowestPrice.amount, lowestPrice.currency)}
                                        </Badge>
                                    )}
                                </Group>
                            ) : (
                                t('game.choosePlatform')
                            )}
                        </Button>
                    </Box>
                )}

                {/* Current Prices Section */}
                {prices.length > 0 && (
                    <Box mb="xl">
                        <Title order={2} mb="md">{t('game.prices')}</Title>
                        <Grid>
                            <Grid.Col span={{ base: 12, sm: selectedStore && isLargeScreen ? 6 : 12 }}>
                                <Stack gap="sm">
                                    {prices.sort((a, b) => parseFloat(a.amount.toString()) - parseFloat(b.amount.toString())).map((price) => {
                                        const isSelected = selectedStore?.storeId === price.store?.id
                                        return (
                                            <Card
                                                key={price.id}
                                                shadow="sm"
                                                padding={isLargeScreen ? "lg" : "md"}
                                                radius="md"
                                                withBorder
                                                style={{
                                                    cursor: isLargeScreen ? 'pointer' : 'default',
                                                    borderColor: isSelected ? 'var(--mantine-color-blue-6)' : undefined,
                                                    borderWidth: isSelected ? 2 : 1
                                                }}
                                                onClick={isLargeScreen ? () => handleShowPriceHistory(price.store?.id || '', price.store?.name || 'Store') : undefined}
                                            >
                                                {isLargeScreen ? (
                                                    // Large screen layout - unchanged
                                                    <Group justify="space-between" align="center">
                                                        <Group align="center" gap="md">
                                                            <div>
                                                                <Group gap="xs" align="center">
                                                                    <Text fw={500} size="lg">{price.store?.name || 'Unknown Store'}</Text>
                                                                    {price.store && (
                                                                        <StoreRating
                                                                            storeId={price.store.id}
                                                                            storeName={price.store.name}
                                                                            averageRating={price.store.rating?.averageRating}
                                                                            totalRatings={price.store.rating?.totalRatings}
                                                                            compact={true}
                                                                            showModal={false}
                                                                            onClose={handleCloseRatingModal}
                                                                            onRateClick={() => handleOpenRatingModal(price.store.id, price.store.name)}
                                                                        />
                                                                    )}
                                                                </Group>
                                                            </div>
                                                        </Group>

                                                        <Group align="center" gap="sm">
                                                            <Badge color="customGreen" variant="light" size="lg">
                                                                {formatPrice(price.amount, price.currency)}
                                                            </Badge>

                                                            {price.storeUrl && (
                                                                <Button
                                                                    component="a"
                                                                    href={price.storeUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    variant="light"
                                                                    size="sm"
                                                                    color='customGreen'
                                                                    rightSection={<IconExternalLink size={14} />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        if (price.store?.id) {
                                                                            trackStoreClick(
                                                                                price.store.id,
                                                                                'INDIVIDUAL_PRICE',
                                                                                parseFloat(price.amount.toString()),
                                                                                price.currency
                                                                            )
                                                                        }
                                                                    }}
                                                                >
                                                                    {t('game.visitStore')}
                                                                </Button>
                                                            )}
                                                        </Group>
                                                    </Group>
                                                ) : (
                                                    // Small screen layout - improved
                                                    <Stack gap="sm">
                                                        {/* Store name and price row */}
                                                        <Group justify="space-between" align="center">
                                                            <div>
                                                                <Group gap="xs" align="center">
                                                                    <Text fw={500} size="md">{price.store?.name || 'Unknown Store'}</Text>
                                                                    {price.store && (
                                                                        <StoreRating
                                                                            storeId={price.store.id}
                                                                            storeName={price.store.name}
                                                                            averageRating={price.store.rating?.averageRating}
                                                                            totalRatings={price.store.rating?.totalRatings}
                                                                            compact={true}
                                                                            showModal={false}
                                                                            onClose={handleCloseRatingModal}
                                                                            onRateClick={() => handleOpenRatingModal(price.store.id, price.store.name)}
                                                                        />
                                                                    )}
                                                                </Group>
                                                            </div>
                                                            <Badge color="customGreen" variant="light" size="lg">
                                                                {formatPrice(price.amount, price.currency)}
                                                            </Badge>
                                                        </Group>

                                                        {/* Action buttons row */}
                                                        <Group gap="xs" justify="space-between">
                                                            <Button
                                                                variant="light"
                                                                color="customGreen"
                                                                size="sm"
                                                                leftSection={<IconTrendingUp size={14} />}
                                                                onClick={() => handleShowPriceHistory(price.store?.id || '', price.store?.name || 'Store')}
                                                                style={{ flex: 1 }}
                                                            >
                                                                {t('game.viewPriceHistory')}
                                                            </Button>

                                                            {price.storeUrl && (
                                                                <Button
                                                                    component="a"
                                                                    href={price.storeUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    variant="filled"
                                                                    color="customGreen"
                                                                    size="sm"
                                                                    rightSection={<IconExternalLink size={14} />}
                                                                    onClick={() => {
                                                                        if (price.store?.id) {
                                                                            trackStoreClick(
                                                                                price.store.id,
                                                                                'INDIVIDUAL_PRICE',
                                                                                parseFloat(price.amount.toString()),
                                                                                price.currency
                                                                            )
                                                                        }
                                                                    }}
                                                                    style={{ flex: 1 }}
                                                                >
                                                                    {t('game.visitStore')}
                                                                </Button>
                                                            )}
                                                        </Group>
                                                    </Stack>
                                                )}
                                            </Card>
                                        )
                                    })}
                                </Stack>
                            </Grid.Col>

                            {/* Price Graph for Large Screens */}
                            {selectedStore && isLargeScreen && (
                                <Grid.Col span={6}>
                                    <Paper shadow="sm" p="lg" radius="md" withBorder>
                                        <Title order={3} mb="md">
                                            Price History - {selectedStore?.storeName}
                                        </Title>
                                        {priceHistoryLoading ? (
                                            <LoadingOverlay visible />
                                        ) : priceHistory && priceHistory.length > 0 ? (
                                            <Box>
                                                <Text size="sm" c="dimmed" mb="md">
                                                    Showing price history for the last 90 days
                                                </Text>
                                                <Box h={400}>
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <LineChart data={formatChartData(priceHistory)}>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis
                                                                dataKey="date"
                                                                tick={{ fontSize: 12 }}
                                                                angle={-45}
                                                                textAnchor="end"
                                                            />
                                                            <YAxis
                                                                tick={{ fontSize: 12 }}
                                                                tickFormatter={(value) => {
                                                                    const currency = priceHistory?.[0]?.currency || 'USD'
                                                                    return `${getCurrencySymbol(currency)}${value}`
                                                                }}
                                                            />
                                                            <Tooltip
                                                                formatter={(value) => {
                                                                    const currency = priceHistory?.[0]?.currency || 'USD'
                                                                    return [`${getCurrencySymbol(currency)}${value}`, 'Price']
                                                                }}
                                                                labelStyle={{ color: '#000' }}
                                                            />
                                                            <Line
                                                                type="monotone"
                                                                dataKey="price"
                                                                stroke="#2563eb"
                                                                strokeWidth={2}
                                                                dot={{ r: 4 }}
                                                                activeDot={{ r: 6 }}
                                                            />
                                                        </LineChart>
                                                    </ResponsiveContainer>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Text c="dimmed">{t('game.noPriceHistory')}</Text>
                                        )}
                                    </Paper>
                                </Grid.Col>
                            )}
                        </Grid>
                    </Box>
                )}

                {/* Comments Section */}
                {currentPlatform && (
                    <Box mb="xl">
                        <CommentSection
                            gameSlug={game.slug}
                            platformSlug={currentPlatform.slug}
                        />
                    </Box>
                )}

                {/* Related Games Section */}
                {relatedGames && relatedGames.length > 0 && (
                    <Box mb="xl">
                        <Title order={2} mb="md">{t('game.relatedGames')}</Title>
                        <HorizontalGameScroller
                            gamePlatforms={relatedGames || []}
                        />
                    </Box>
                )}

                {/* Product IDs Section */}
                {((productIds && productIds.length > 0)) && (
                    <Box mb="xl">
                        <Title order={2} mb="md">{t('game.productInformation')}</Title>
                        {productIds.map((productId: any) => (
                            <Badge key={productId.id} variant="light" size="lg" mr="md" color="customGreen">
                                {productId.type}: {productId.value}
                            </Badge>
                        ))}
                    </Box>
                )}

            </Container>

            {/* Add to Wishlist Modal */}
            <Modal
                opened={wishlistModal.open}
                onClose={() => setWishlistModal({ open: false, maxPrice: 0, isEditing: false })}
                title={wishlistModal.isEditing ? t('game.wishlistModal.editTitle') : t('game.wishlistModal.title')}
                size="md"
            >
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        {wishlistModal.isEditing ? t('game.wishlistModal.editDescription') : t('game.wishlistModal.description')}
                    </Text>

                    {lowestPrice && (
                        <Box p="sm" bg="gray.1" style={{ borderRadius: '8px' }}>
                            <Text size="sm" fw={500} mb="xs">
                                {t('game.wishlistModal.currentPrice')}
                            </Text>
                            <Badge color="green" variant="light" size="lg">
                                {formatPrice(lowestPrice.amount, lowestPrice.currency)}
                            </Badge>
                            <Text size="xs" c="dimmed" mt="xs">
                                at {lowestPrice.store?.name}
                            </Text>
                        </Box>
                    )}

                    <NumberInput
                        label={t('game.wishlistModal.maxPriceLabel')}
                        placeholder={t('game.wishlistModal.maxPricePlaceholder')}
                        value={wishlistModal.maxPrice}
                        onChange={(value) => setWishlistModal(prev => ({ ...prev, maxPrice: typeof value === 'number' ? value : 0 }))}
                        min={0.01}
                        step={0.01}
                        decimalScale={2}
                        prefix={lowestPrice ? getCurrencySymbol(lowestPrice.currency) : '$'}
                        required
                    />

                    <Group justify="space-between" gap="sm">
                        {wishlistModal.isEditing && (
                            <Button
                                variant="outline"
                                color="red"
                                leftSection={<IconTrash size={16} />}
                                onClick={handleRemoveFromWishlist}
                            >
                                {t('game.removeFromWishlist')}
                            </Button>
                        )}
                        <Group gap="sm" ml="auto">
                            <Button
                                variant="subtle"
                                onClick={() => setWishlistModal({ open: false, maxPrice: 0, isEditing: false })}
                            >
                                {t('game.wishlistModal.cancel')}
                            </Button>
                            <Button
                                onClick={handleConfirmAddToWishlist}
                                disabled={wishlistModal.maxPrice <= 0}
                            >
                                {wishlistModal.isEditing ? t('game.wishlistModal.update') : t('game.wishlistModal.add')}
                            </Button>
                        </Group>
                    </Group>
                </Stack>
            </Modal>

            {/* Price History Modal - Only show on mobile/tablet */}
            {!isLargeScreen && (
                <Modal
                    opened={priceHistoryModal.open}
                    onClose={() => setPriceHistoryModal({ open: false })}
                    title={`Price History - ${priceHistoryModal.storeName}`}
                    size="xl"
                >
                    {priceHistoryLoading ? (
                        <LoadingOverlay visible />
                    ) : priceHistory && priceHistory.length > 0 ? (
                        <Box>
                            <Text size="sm" c="dimmed" mb="md">
                                Showing price history for the last 90 days
                            </Text>
                            <Box h={400}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={formatChartData(priceHistory)}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 12 }}
                                            angle={-45}
                                            textAnchor="end"
                                        />
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            tickFormatter={(value) => {
                                                const currency = priceHistory?.[0]?.currency || 'USD'
                                                return `${getCurrencySymbol(currency)}${value}`
                                            }}
                                        />
                                        <Tooltip
                                            formatter={(value) => {
                                                const currency = priceHistory?.[0]?.currency || 'USD'
                                                return [`${getCurrencySymbol(currency)}${value}`, 'Price']
                                            }}
                                            labelStyle={{ color: '#000' }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="price"
                                            stroke="#2563eb"
                                            strokeWidth={2}
                                            dot={{ r: 4 }}
                                            activeDot={{ r: 6 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Box>
                        </Box>
                    ) : (
                        <Text c="dimmed">{t('game.noPriceHistory')}</Text>
                    )}
                </Modal>
            )}

            {/* Platform Selection Modal */}
            <Modal
                opened={platformModal}
                onClose={() => setPlatformModal(false)}
                title="Select Platform"
                size="md"
            >
                <Stack gap="sm">
                    {gamePlatforms?.filter((gamePlatform: any) =>
                        gamePlatform.platform?.slug &&
                        gamePlatform.platform.slug.trim() !== ''
                    ).map((gamePlatform: any) => {
                        const lowestPrice = getLowestPrice(gamePlatform.prices || [])
                        const isCurrentPlatform = gamePlatform.platform.slug === platformSlug

                        return (
                            <Card
                                key={gamePlatform.platform.slug}
                                shadow="sm"
                                padding="md"
                                radius="md"
                                withBorder
                                style={{
                                    cursor: 'pointer',
                                    borderColor: isCurrentPlatform ? `var(--mantine-color-${getPlatformColor(gamePlatform.platform)}-6)` : undefined,
                                    borderWidth: isCurrentPlatform ? 2 : 1,
                                    backgroundColor: isCurrentPlatform ? `var(--mantine-color-${getPlatformColor(gamePlatform.platform)}-0)` : undefined
                                }}
                                onClick={() => {
                                    handlePlatformChange(gamePlatform.platform.slug)
                                    setPlatformModal(false)
                                }}
                            >
                                <Group justify="space-between" align="center">
                                    <Group align="center" gap="md">
                                        <div>
                                            <Text fw={500} size="md">
                                                {gamePlatform.platform.name}
                                            </Text>
                                        </div>
                                    </Group>

                                    {lowestPrice && (
                                        <Badge color="green" variant="light" size="lg">
                                            {formatPrice(lowestPrice.amount, lowestPrice.currency)}
                                        </Badge>
                                    )}
                                </Group>
                            </Card>
                        )
                    })}
                </Stack>
            </Modal>

            {/* Game Rating Modal */}
            {isGamePlatform && currentPlatform && (
                <Modal
                    opened={ratingModal.open}
                    onClose={handleCloseRatingModal}
                    title={t('game.rating.rateTitle', { gameTitle: game.title })}
                    size="md"
                >
                    <GameRating
                        gameSlug={game.slug}
                        platformSlug={currentPlatform.slug}
                        gameTitle={game.title}
                        averageRating={((gameData as any)?.userReviewScore ?? 0)}
                        totalRatings={((gameData as any)?.userReviewCount ?? 0)}
                        showModal={false}
                        onClose={handleCloseRatingModal}
                    />
                </Modal>
            )}

            {/* Image Browser Modal */}
            {isGamePlatform && currentPlatform && (
                <ImageBrowserModal
                    opened={imageBrowserModalOpened}
                    onClose={handleCloseImageBrowser}
                    onSelect={handleSelectImage}
                    gameId={game.id}
                    platformId={currentPlatform.id}
                    title={`Select Image for ${currentPlatform.name}`}
                />
            )}
        </>
    )
} 